// src/components/Finanzas/RevisionCierre.jsx
// Pantalla intermedia entre iniciar cierre y aprobar.
// Recicla patrón visual: cards resumen, alertas bloqueantes, tabla preview de comprobantes.

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Button, Badge, Alert, Form, Modal } from 'react-bootstrap';
import { FaClipboardCheck, FaFileInvoiceDollar, FaExclamationTriangle, FaCheckCircle, FaTimes, FaFilePdf, FaExchangeAlt } from 'react-icons/fa';
import { Alertassw } from '../Alertas/Alertassw';
import {
  obtenerRelacion,
  previewRelacion,
  aprobarRelacion,
  cancelarRelacion,
  listarEntregas,
  listarTraslados
} from '../../api/finanzas';
import { extractError } from './utils/extractError';
import { useAuth } from '../../hooks/useAuth';
import { APPROVE_RELACIONES } from '../Roles/ConstantRoute';
import TablaMovimientosCierre from './TablaMovimientosCierre';
import { descargarPdfRelacionLocal } from './utils/descargarPdfHelper';

const formatoCOP = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;

const RevisionCierre = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [relacion, setRelacion] = useState(null);
  const [preview, setPreview] = useState(null);
  const [entregas, setEntregas] = useState([]);
  const [traslados, setTraslados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [observaciones, setObservaciones] = useState('');
  const [aprobando, setAprobando] = useState(false);
  const [descargandoPdf, setDescargandoPdf] = useState(false);
  const [erroresValidacion, setErroresValidacion] = useState(null);

  const {userData} = useAuth();

  useEffect(() => {
    cargar();
  }, [id]);

  const cargar = async () => {
    setLoading(true);
    try {
      const [rel, prev] = await Promise.all([
        obtenerRelacion(id),
        previewRelacion(id)
      ]);
      const relacionData = rel?.status === 200 ? rel.data.data : null;
      if (relacionData) setRelacion(relacionData);
      if (prev?.status === 200) setPreview(prev.data.data);

      // Hidratar entregas + traslados del periodo en paralelo. El backend ya
      // expone los _id en relacion.entregasIncluidas / trasladosIncluidos.
      const entregasIds  = relacionData?.entregasIncluidas  || [];
      const trasladosIds = relacionData?.trasladosIncluidos || [];
      const [respEntregas, respTraslados] = await Promise.all([
        entregasIds.length
          ? listarEntregas({ conditions: { _id: { $in: entregasIds } }, sort: { fecha: -1 } })
          : Promise.resolve({ data: { data: [] } }),
        trasladosIds.length
          ? listarTraslados({ conditions: { _id: { $in: trasladosIds } }, sort: { fecha: -1 } })
          : Promise.resolve({ data: { data: [] } })
      ]);
      setEntregas(respEntregas?.data?.data || []);
      setTraslados(respTraslados?.data?.data || []);
    } catch (err) {
      console.error(err);
      Alertassw('error', 'Error', 'No se pudo cargar la relación');
    } finally {
      setLoading(false);
    }
  };

  const handleDescargarPdf = async () => {
    setDescargandoPdf(true);
    try {
      await descargarPdfRelacionLocal({ relacion });
    } finally {
      setDescargandoPdf(false);
    }
  };

  const handleAprobar = async () => {
    setErroresValidacion(null);
    setAprobando(true);
    try {
      const resp = await aprobarRelacion(id, observaciones);
      if (resp?.status === 200) {
        Alertassw('success', 'Cierre aprobado', 'Se generaron los comprobantes y se disparó la sincronización.');
        navigate(`/MisRelaciones`);
      }
    } catch (err) {
      const data = err?.response?.data;
      if (data?.codigo === 'CIERRE_VALIDACION') {
        setErroresValidacion(data);
      } else {
        Alertassw('error', 'Error al aprobar', extractError(err));
      }
    } finally {
      setAprobando(false);
    }
  };

  const handleCancelar = async () => {
    if (!window.confirm('¿Cancelar esta relación? Los gastos volverán a quedar disponibles.')) return;
    try {
      await cancelarRelacion(id);
      Alertassw('success', 'Cancelada', 'La relación fue cancelada.');
      navigate(`/MiBolsa`);
    } catch (err) {
      Alertassw('error', 'Error', extractError(err));
    }
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" />
          <p className="mt-3">Cargando relación...</p>
        </div>
      </Container>
    );
  }

  if (!relacion || !preview) {
    return (
      <Container fluid className="py-4">
        <Alert variant="warning">No se encontró la relación.</Alert>
      </Container>
    );
  }

  const bloqueado = !!erroresValidacion;
  const mesesPreview = preview.mesesRepresentados || [];

  return (
    <Container fluid className="py-4 w-100">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h2 className="text-primary fw-bold">
            <FaClipboardCheck className="me-2" />
            Revisión de cierre — {relacion.consecutivo}
          </h2>
          <p className="text-muted">Revisa el detalle antes de aprobar el cierre del período</p>
        </Col>
      </Row>

      {/* Cards resumen */}
      <Row className="mb-4 g-2">
        <Col md={3}>
          <Card className="h-100 shadow-sm">
            <Card.Body className="text-center">
              <FaFileInvoiceDollar className="text-primary mb-2" size={30} />
              <h6 className="text-muted mb-2">Bolsillo</h6>
              <strong>{preview.relacion?.bolsillo?.nombre || '—'}</strong>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 shadow-sm">
            <Card.Body className="text-center">
              <h6 className="text-muted mb-2">Meses cubiertos</h6>
              <h4 className="fw-bold mb-0 text-primary">{mesesPreview.length}</h4>
              <small className="text-muted">{mesesPreview.join(', ')}</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 shadow-sm">
            <Card.Body className="text-center">
              <h6 className="text-muted mb-2">Total gastos</h6>
              <h4 className="fw-bold mb-0 text-danger">{formatoCOP(relacion.montoTotalGastos)}</h4>
              <small className="text-muted">{relacion.gastosIncluidos?.length || 0} gasto(s)</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 shadow-sm">
            <Card.Body className="text-center">
              <h6 className="text-muted mb-2">Comprobantes a generar</h6>
              <h4 className="fw-bold mb-0 text-success">{mesesPreview.length}</h4>
              <small className="text-muted">uno por mes calendario</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Errores de validación */}
      {erroresValidacion && (
        <Alert variant="danger">
          <FaExclamationTriangle className="me-2" />
          <strong>{erroresValidacion.error}</strong>
          {erroresValidacion.gastosSinTercero?.length > 0 && (
            <div className="mt-2 small">
              {erroresValidacion.gastosSinTercero.length} gasto(s) con factura sin tercero asignado.
              Edita esos gastos para asignar el proveedor antes de aprobar.
            </div>
          )}
          {erroresValidacion.gastosSinOT?.length > 0 && (
            <div className="mt-2 small">
              {erroresValidacion.gastosSinOT.length} gasto(s) sin Orden de Trabajo en cuentas que la requieren.
              Edita esos gastos y asigna una OT antes de aprobar.
            </div>
          )}
        </Alert>
      )}

      {/* Preview de comprobantes por mes */}
      <Card className="mb-4 shadow-sm w-100">
        <Card.Header className="bg-light w-100">
          <h5 className="mb-0">Preview de los comprobantes que se generarán</h5>
        </Card.Header>
        <Card.Body className="w-100">
          {mesesPreview.map((mes) => {
            const datos = preview.gastosPorMes[mes];
            return (
              <div key={mes} className="mb-4">
                <h6 className="fw-bold mb-2">
                  Comprobante mensual — {mes}
                  <Badge bg="info" className="ms-2">{datos.gastos.length} gasto(s)</Badge>
                </h6>
                <Table hover size="sm" responsive>
                  <thead className="table-light">
                    <tr>
                      <th>Fecha</th>
                      <th>Categoría</th>
                      <th>Descripción</th>
                      <th>Factura</th>
                      <th className="text-end">Valor total</th>
                      <th className="text-end">IVA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.gastos.map((g) => (
                      <tr key={g._id}>
                        <td>{new Date(g.fecha).toLocaleDateString('es-CO')}</td>
                        <td><small>{g.categoria}</small></td>
                        <td><small>{g.descripcion}</small></td>
                        <td>
                          {g.tieneFactura ? (
                            <Badge bg="primary">{g.numeroFactura || 'sí'}</Badge>
                          ) : (
                            <small className="text-muted">—</small>
                          )}
                        </td>
                        <td className="text-end fw-bold">{formatoCOP(g.valorTotal)}</td>
                        <td className="text-end">{formatoCOP(g.iva || 0)}</td>
                      </tr>
                    ))}
                    <tr className="table-secondary">
                      <td colSpan={4} className="text-end fw-bold">Total mes:</td>
                      <td className="text-end fw-bold">{formatoCOP(datos.total)}</td>
                      <td></td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            );
          })}
        </Card.Body>
      </Card>

      {/* Movimientos de dinero incluidos en el cierre */}
      <Card className="mb-4 shadow-sm w-100">
        <Card.Header className="bg-light w-100">
          <h5 className="mb-0">
            <FaExchangeAlt className="me-2" />
            Movimientos de dinero incluidos ({entregas.length + traslados.length})
          </h5>
          <small className="text-muted">Entregas y traslados capturados en este cierre.</small>
        </Card.Header>
        <Card.Body className="w-100">
          <TablaMovimientosCierre
            entregas={entregas}
            traslados={traslados}
            bolsilloId={relacion.bolsillo?._id || relacion.bolsillo}
          />
        </Card.Body>
      </Card>

      {/* Observaciones */}
      <Card className="mb-4 shadow-sm w-100">
        <Card.Body className="w-100">
          <Form.Label>Observaciones del cierre</Form.Label>
          <Form.Control as="textarea" rows={3}
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            placeholder="Notas opcionales sobre este cierre..."
          />
        </Card.Body>
      </Card>

      {/* Acciones */}
      <Row className="g-2">
        <Col xs="auto">
          <Button
            variant="outline-primary"
            onClick={handleDescargarPdf}
            disabled={descargandoPdf}
          >
            <FaFilePdf className="me-1" />
            {descargandoPdf ? 'Generando...' : 'Descargar PDF'}
          </Button>
        </Col>
        <Col xs="auto">
          <Button variant="outline-danger" onClick={handleCancelar} disabled={aprobando}>
            <FaTimes className="me-1" /> Cancelar relación
          </Button>
        </Col>
        <Col xs="auto">
        {userData?.role?.permisos?.indexOf(APPROVE_RELACIONES) !== -1 && (
          <Button variant="success" size="lg" onClick={handleAprobar} disabled={aprobando || bloqueado}>
            <FaCheckCircle className="me-1" />
            {aprobando ? 'Aprobando...' : 'Aprobar y generar comprobantes'}
          </Button>
        )}
        </Col>
      </Row>
    </Container>
  );
};

export default RevisionCierre;
